import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Loader2, Search, Users, Trash2, Gauge, DollarSign } from "lucide-react";

const CHIP = ["#0044FF", "#10B981", "#7C3AED", "#F59E0B", "#EC4899", "#0EA5E9", "#EF4444", "#14B8A6"];
const blank = { name: "", role: "", email: "", daily_rate: 0, currency: "USD", capacity: 1.0, tags: [], notes: "" };

export default function LibraryResources() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    const { data } = await api.get(`/library-resources?${params.toString()}`);
    setItems(data);
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setForm(blank); setEditId(null); setOpen(true); };
  const openEdit = (r) => { setForm({ ...blank, ...r, tags: r.tags || [] }); setEditId(r.id); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form, daily_rate: Number(form.daily_rate || 0), capacity: Number(form.capacity || 1),
        tags: typeof form.tags === "string" ? form.tags.split(",").map((s) => s.trim()).filter(Boolean) : form.tags,
      };
      if (editId) await api.put(`/library-resources/${editId}`, payload);
      else await api.post(`/library-resources`, payload);
      setOpen(false);
      toast.success(editId ? "Updated" : "Saved to library");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => { await api.delete(`/library-resources/${id}`); setOpen(false); toast.success("Removed"); load(); };

  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, role or tag…" className="pl-9" data-testid="libres-search-input" />
        </div>
        <Button className="gap-2" onClick={openNew} data-testid="libres-add-button"><Plus className="h-4 w-4" /> New resource</Button>
      </div>

      {items === null ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-border rounded-lg p-12 text-center" data-testid="libres-empty">
          <Users className="h-8 w-8 text-primary mx-auto mb-3" />
          <p className="text-muted-foreground">No saved resources yet. Add people or roles here once, then import them into any project's Budget tab.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="libres-grid">
          {items.map((r, idx) => (
            <button key={r.id} onClick={() => openEdit(r)} className="text-left border border-border rounded-lg p-5 hover:-translate-y-1 hover:shadow-lg hover:border-foreground/30 transition-all bg-card" data-testid={`libres-item-${r.id}`}>
              <div className="flex items-center gap-3 mb-3">
                <span className="h-10 w-10 rounded-lg flex items-center justify-center shadow-sm shrink-0" style={{ background: CHIP[idx % CHIP.length], boxShadow: `0 4px 12px -2px ${CHIP[idx % CHIP.length]}66` }}>
                  <Users className="h-5 w-5 text-white" strokeWidth={2.5} />
                </span>
                <div className="min-w-0">
                  <h3 className="font-display font-bold tracking-tight truncate">{r.name}</h3>
                  <p className="text-xs text-muted-foreground truncate">{r.role || "—"}</p>
                </div>
              </div>
              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1"><DollarSign className="h-3.5 w-3.5" /> {r.daily_rate}/day {r.currency}</span>
                <span className="inline-flex items-center gap-1"><Gauge className="h-3.5 w-3.5" /> {r.capacity} FTE</span>
              </div>
              {(r.tags || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {r.tags.map((tg) => <span key={tg} className="text-[11px] px-2 py-0.5 rounded bg-secondary text-muted-foreground">#{tg}</span>)}
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="libres-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editId ? "Edit" : "New"} resource</DialogTitle>
            <DialogDescription>Reusable across all your projects — import from any Budget tab.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="lr-name">Name</Label>
                <Input id="lr-name" required value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="libres-name-input" className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="lr-role">Role</Label>
                <Input id="lr-role" value={form.role} onChange={(e) => set("role", e.target.value)} placeholder="e.g. Backend Engineer" data-testid="libres-role-input" className="mt-1.5" />
              </div>
              <div className="col-span-2">
                <Label htmlFor="lr-email">Email <span className="text-muted-foreground font-normal">(used for Cards digests)</span></Label>
                <Input id="lr-email" type="email" value={form.email || ""} onChange={(e) => set("email", e.target.value)} placeholder="person@company.com" data-testid="libres-email-input" className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="lr-rate">Daily rate</Label>
                <Input id="lr-rate" type="number" value={form.daily_rate} onChange={(e) => set("daily_rate", e.target.value)} data-testid="libres-rate-input" className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="lr-cap">Capacity (FTE)</Label>
                <Input id="lr-cap" type="number" min="0.1" step="0.1" value={form.capacity} onChange={(e) => set("capacity", e.target.value)} data-testid="libres-capacity-input" className="mt-1.5" />
              </div>
            </div>
            <div>
              <Label htmlFor="lr-tags">Tags (comma-separated)</Label>
              <Input id="lr-tags" value={Array.isArray(form.tags) ? form.tags.join(", ") : form.tags} onChange={(e) => set("tags", e.target.value)} placeholder="contractor, design" data-testid="libres-tags-input" className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="lr-notes">Notes</Label>
              <Textarea id="lr-notes" rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} className="mt-1.5" />
            </div>
            <DialogFooter className="flex sm:justify-between gap-2">
              {editId && <Button type="button" variant="outline" className="text-destructive border-destructive/30 hover:bg-destructive/10 gap-2" onClick={() => remove(editId)} data-testid="libres-delete-button"><Trash2 className="h-4 w-4" /> Delete</Button>}
              <Button type="submit" disabled={saving} data-testid="libres-save-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
